/* eslint-disable prefer-destructuring */
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
} from "draft-js";

import uuid from "react-native-uuid";
import HTMLtoDraft from "./HTMLtoDraft/HTMLtoDraft";
import { DOCUMENTS, COMMON, DRAFT, ERROR_MSG } from "../constants";

// Internal Imports
import { normaliseTableData } from "./tableUtils";
import {
  convertSectionRawToDraft,
  isDraftRawDataObj,
  updateSectionDataForEntity,
} from "./components";
import { numberForamting } from "./globalUtils";
import {
  Signer,
  SubmitSignatory,
  SignerConfig,
} from "../payloadObjects/signatoryPayload";

// Destructuring
const { CURRENCY_SYMBOL, DEFAULT_CURRENCY } = COMMON;
const { DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
const { API_ERRORS } = ERROR_MSG;
/*
Convert the raw state of data to draft state for html
*/

export const convertRawToDraft = data => {
  try {
    if (
      data &&
      typeof data === "object" &&
      DRAFT.DRAFT_RAW_KEYS.length === Object.keys(data).length &&
      JSON.stringify(DRAFT.DRAFT_RAW_KEYS.sort()) ===
        JSON.stringify(Object.keys(data).sort())
    ) {
      return convertFromRaw(data);
    }
    console.error(`Raw to draft conversion error. Raw keys unavailable`);
    const editorState = EditorState.createEmpty();
    return editorState.getCurrentContent();
  } catch (error) {
    throw Error(
      `Unable to convert raw state to draft state. Error: ${error.message}`,
    );
  }
};

export const convertHTMLStringToDraft = (html, mode) => {
  try {
    return HTMLtoDraft(html, mode);
  } catch (error) {
    console.error(
      `Unable to convert the html object into editor object. Error: ${error.message}`,
    );
    throw Error(API_ERRORS.DEFAULT_MSG);
  }
};

export const convertDocumentContentV2 = (components, mode) => {
  Object.keys(components).forEach(componentId => {
    let section = components[componentId];
    if (section) {
      section = convertSectionRawToDraft(section.content, mode);
    }
    components[componentId] = {
      content: section,
      dbRowIndex: components[componentId].dbRowIndex,
      dbColIndex: components[componentId].dbColIndex,
    };
  });
  return components;
};

export const createTableRevision = table => {
  const tableData = table.data.map(row => {
    row.data.forEach(col => {
      if (typeof col.data === "object") {
        // reseting diffHtml & diffState of each cell so previous version's diffs don't show in new versions
        if ("diffHtml" in col.data) col.data.diffHtml = "";
        if ("diffState" in col.data) col.data.diffState = {};
      }
    });
    return row;
  });
  return { ...table, data: tableData };
};

export const createComponentListRevision = sectionList =>
  Array.isArray(sectionList)
    ? // eslint-disable-next-line no-use-before-define
      sectionList.map(section => createComponentRevision(section))
    : [];

export const createComponentRevision = section => {
  const newSection = {
    ...section,
  };
  if (section.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    newSection.data = createTableRevision(section.data);
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    if (section.data instanceof ContentState) {
      newSection.data = convertToRaw(section.data);
    }
    delete newSection.currentContent;
    delete newSection.diffHtml;
    delete newSection.diffState;
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    newSection.data = {
      ...section.data,
      data: createComponentListRevision(section.data.data),
    };
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    newSection.data = {
      ...section.data,
      footer: {
        ...section.data.footer,
        data: createComponentListRevision(section.data.footer.data),
      },
      header: {
        ...section.data.header,
        data: createComponentListRevision(section.data.header.data),
      },
    };
  }
  return newSection;
};

export const createDocumentRevision = sectionList => {
  const componentList = [];
  const revisionList = createComponentListRevision(sectionList);
  revisionList.forEach(section => {
    // create a new section
    const newSection = {
      section_id: uuid.v4(),
      section_type: section.type,
      section_name: section.label,
      lock: section.lock,
      data: section.data,
      html: section.html,
      clauseID: section.clauseID,
      form_field: section.form_field,
      clauseOptions: section.clauseOptions,
    };
    if (section.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
      newSection.sheet = section.sheet;
      newSection.workSheet = section.workSheet;
      newSection.rangeValue = section.rangeValue;
      newSection.autoTrim = section.autoTrim;
      newSection.connected = section.connected;
      newSection.firstRowAsHeader = section.firstRowAsHeader;
    }
    // check if the content in an object
    componentList.push({
      component_id: newSection.section_id,
      content: {
        ...newSection,
      },
    });
  });
  return componentList;
};

export const getFieldValue = (field, currency) => {
  if (!currency) {
    currency = DEFAULT_CURRENCY;
  }
  const { value: fieldValue, type } = field;

  if (type === "NUMBER") {
    if (fieldValue === null || fieldValue === undefined || fieldValue === "") {
      return "";
    }
  } else if (!fieldValue) {
    return "";
  }

  let value;
  if (field.type === "PERIOD") {
    const { periodValue, periodUom } = fieldValue;
    if (periodValue && periodUom) {
      value = `${periodValue || ""} ${periodUom || ""}`;
    }
  } else if (field.type === "ADDRESS") {
    const { addressLineOne, addressLineTwo, city, state, zip, country } =
      fieldValue;
    if (addressLineOne || addressLineTwo || city || state || country) {
      value = `${addressLineOne ? `${addressLineOne},` : ""}${
        addressLineTwo ? `${addressLineTwo},` : ""
      }${city ? `${city},` : ""}${state ? `${state},` : ""}${
        zip ? `- ${zip},` : ""
      }${country ? `(${country})` : ""}`;
    } else {
      value = null;
    }
  } else if (field.type === "PERSON_NAME") {
    const { firstName, lastName } = fieldValue;
    value = firstName || "";
    value = lastName ? `${value} ${lastName}` : value;
  } else if (field.type === "AMOUNT") {
    let currencyInfo = currency;

    if (
      Object.keys(fieldValue).length !== 0 &&
      fieldValue.currency &&
      fieldValue.currency !== "PRICEBOOK"
    ) {
      currencyInfo = fieldValue.currency;
    }

    const symbol = CURRENCY_SYMBOL[currencyInfo];
    if (
      fieldValue &&
      !(
        fieldValue.val === null ||
        fieldValue.val === undefined ||
        fieldValue.val === ""
      )
    ) {
      value = numberForamting(fieldValue.val, symbol);
    } else if (
      (typeof fieldValue === "string" || typeof fieldValue === "number") &&
      fieldValue !== undefined &&
      fieldValue !== null &&
      fieldValue !== ""
    ) {
      value = numberForamting(fieldValue, symbol);
    } else {
      value = null;
    }
  } else if (field.type === "PERCENT") {
    if (fieldValue !== "") {
      value = numberForamting(fieldValue, "", "%");
    } else {
      value = fieldValue;
    }
  } else if (field.type === "RATE") {
    const { rateValue, rateUom } = fieldValue;
    if (rateValue === "" || rateUom === "") {
      value = "";
    } else {
      value = `${rateValue} ${rateUom}`;
    }
  } else if (field.type === "CHECKBOX") {
    value = Array.isArray(fieldValue) ? fieldValue.join(", ") : "";
  } else if (field.type === "DATE") {
    value = fieldValue && fieldValue.val ? fieldValue.val : fieldValue || "";
  } else if (field.type === "SIGNATURE") {
    if (typeof fieldValue === "string") {
      value = fieldValue;
    } else {
      value = `${fieldValue.firstName ? fieldValue.firstName : ""} ${
        fieldValue.lastName ? fieldValue.lastName : ""
      }`;
    }
  } else if (field.type === "CLAUSE") {
    const {
      meta: { subtype: subType, default: defaultValue } = {},
      value: mappedValue,
    } = field;
    if (subType === "free_text") {
      if (defaultValue !== mappedValue) {
        value = mappedValue;
      } else {
        value = defaultValue
          ? convertFromRaw(JSON.parse(defaultValue)).getPlainText()
          : "";
      }
    }
  } else {
    value = fieldValue;
  }
  return value;
};

export const fillFormFieldinEntity = (textBlock, fieldsUsed, currency) => {
  const { id, section_id: sectionId, data: rawData } = textBlock;
  if (Array.isArray(fieldsUsed) && fieldsUsed.length > 0) {
    const { entityMap } = rawData;
    Object.keys(entityMap).forEach(entityKey => {
      const entity = entityMap[entityKey];
      if (entity.type === "DYNAMICFIELD") {
        const fieldDesc = fieldsUsed.find(
          f => f.id === entity.data.dynamicFieldId,
        );
        if (fieldDesc && entity.value !== fieldDesc.value) {
          const fieldValue = {
            ...fieldDesc,
            value: fieldDesc.value,
          };
          entityMap[entityKey].data.value = getFieldValue(fieldValue, currency);
          entityMap[entityKey].data.label = fieldDesc.label;
          entityMap[entityKey].data.sectionId = sectionId || id;
        }
      }
    });
  }
  return rawData;
};

export const fillFormInTextBlock = (textBlock, fieldsUsed, currency) => {
  const { data: textData } = textBlock;
  if (typeof textData === "object") {
    return fillFormFieldinEntity(textBlock, fieldsUsed, currency);
  }
  return textData;
};

export const fillFormInTableBlock = (tableSection, fieldsUsed, currency) => {
  const { clauseOptions } = tableSection;
  let { data: updatedTable } = tableSection;
  if (clauseOptions && clauseOptions.id) {
    const clauseField = fieldsUsed.find(field => field.id === clauseOptions.id);
    if (clauseField && clauseField.value && clauseField.type !== "TABLE_API") {
      if (clauseField.value.value) {
        updatedTable = clauseField.value.value;
      } else {
        updatedTable = clauseField.value;
      }
    }
  } else {
    const hiddenCols =
      updatedTable.attributes &&
      Array.isArray(updatedTable.attributes.hiddenCols)
        ? updatedTable.attributes.hiddenCols
        : [];
    const nData = normaliseTableData(
      updatedTable,
      DOCUMENT_COMPONENT_TYPES.TABLE,
      hiddenCols,
    );
    const { data: tableData } = nData;
    updatedTable.data = tableData.map(row => {
      const { data: rowData } = row;
      row.data = rowData.map(cell => {
        const { data: cellData } = cell;
        const textBlock = {
          ...cellData,
          id: cell.attributes.id,
        };
        if (textBlock.data)
          cellData.data = fillFormInTextBlock(textBlock, fieldsUsed, currency);
        return cell;
      });
      return row;
    });
  }
  return updatedTable;
};

export const fillFormFieldInSection = (component, fieldsUsed, currency) => {
  let section;
  if (component.content) {
    section = component.content;
  } else {
    section = component;
  }
  const sectionType = section.type || section.section_type;
  if (sectionType === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    if (section.data.data)
      // eslint-disable-next-line no-use-before-define
      section.data.data = fillFormFieldInLayout(
        section.data.data,
        fieldsUsed,
        currency,
      );
    else {
      // eslint-disable-next-line no-use-before-define
      section.data.components = fillFormFieldInLayout(
        section.data.components,
        fieldsUsed,
        currency,
      );
    }
  } else if (sectionType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    if (section.data.header.data)
      // eslint-disable-next-line no-use-before-define
      section.data.header.data = fillFormFieldInLayout(
        section.data.header.data,
        fieldsUsed,
        currency,
      );
    else {
      // eslint-disable-next-line no-use-before-define
      section.data.header.components = fillFormFieldInLayout(
        section.data.header.components,
        fieldsUsed,
        currency,
      );
    }
    if (section.data.footer.data)
      // eslint-disable-next-line no-use-before-define
      section.data.footer.data = fillFormFieldInLayout(
        section.data.footer.data,
        fieldsUsed,
        currency,
      );
    else {
      // eslint-disable-next-line no-use-before-define
      section.data.footer.components = fillFormFieldInLayout(
        section.data.footer.components,
        fieldsUsed,
        currency,
      );
    }
  } else if (sectionType === DOCUMENT_COMPONENT_TYPES.TABLE) {
    section.data = fillFormInTableBlock(section, fieldsUsed, currency);
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    section.data = fillFormInTextBlock(section, fieldsUsed, currency);
  }

  if (component.content) {
    return {
      ...component,
      content: {
        ...section,
      },
    };
  }

  return section;
};

export const fillFormFieldInaRow = (sectionList, fieldsUsed, currency) => {
  // this section could be a an array or a single component
  // array is for newer layout, i.e. n-column layout
  // a single component is for older layout
  if (Array.isArray(sectionList)) {
    return sectionList.map(section =>
      fillFormFieldInSection(section, fieldsUsed, currency),
    );
  }
  if (typeof sectionList === "object") {
    return fillFormFieldInSection(sectionList, fieldsUsed, currency);
  }
  return sectionList;
};

export const fillFormFieldInLayout = (sectionList, fieldsUsed, currency) => {
  // this section should be a layout of document
  // it could be in new format of 2-D or 1-D
  // 2-D is for newer layout, i.e. n-column layout
  // 1-D id older layout
  if (Array.isArray(sectionList)) {
    const filledSections = sectionList.map(sections => {
      if (Array.isArray(sections)) {
        return fillFormFieldInaRow(sections, fieldsUsed, currency);
      }
      return fillFormFieldInSection(sections, fieldsUsed, currency);
    });
    return filledSections;
  }
  if (Object.keys(sectionList).length > 0) {
    const filledSections = {};
    Object.keys(sectionList).forEach(sectionID => {
      const sections = sectionList[sectionID];
      if (Array.isArray(sections)) {
        filledSections[sectionID] = {
          ...fillFormFieldInaRow(sections, fieldsUsed, currency),
        };
        // return fillFormFieldInaRow(sections, fieldsUsed, currency);
      } else
        filledSections[sectionID] = {
          ...fillFormFieldInSection(sections, fieldsUsed, currency),
        };
      // return fillFormFieldInSection(sections, fieldsUsed, currency);
    });
    return filledSections;
  }
  return sectionList;
};

export const fillFormField = (
  sectionList,
  documentType,
  fieldsUsed,
  currency,
) => fillFormFieldInLayout(sectionList, fieldsUsed, currency);

export const fillFormFieldV2 = (components, mode, fieldsUsed, currency) => {
  const filledComponents = {};
  Object.keys(components).forEach(componentId => {
    const section = components[componentId];
    const updatedComponent = fillFormFieldInSection(
      section,
      fieldsUsed,
      currency,
    );
    filledComponents[componentId] = updatedComponent;
  });
  return filledComponents;
};

export const applyNoteOnClauseOptionField = (
  value,
  item,
  noteMappingConfigs,
) => {
  let updatedValue = value;
  const jsonClauseValue = value;
  let rawValue = jsonClauseValue;
  if (typeof jsonClauseValue === "string") {
    if (jsonClauseValue.startsWith("<")) {
      rawValue = jsonClauseValue;
    } else if (jsonClauseValue.startsWith("{")) {
      rawValue = JSON.parse(jsonClauseValue);
    }
  }

  if (isDraftRawDataObj(rawValue)) {
    let isNoteEntityPresent = false;
    if (rawValue.entityMap && Object.values(rawValue.entityMap).length > 0) {
      Object.values(rawValue.entityMap).forEach(entity => {
        if (
          entity.type === "NOTES" &&
          entity.data &&
          entity.data.id === item.anchor.id
        ) {
          isNoteEntityPresent = true;
        }
      });
    }
    if (!isNoteEntityPresent) {
      const { anchorKey, acnhorOffset, focusKey, focusOffset, length } =
        noteMappingConfigs.selectionData;
      rawValue = updateSectionDataForEntity(
        rawValue,
        anchorKey,
        acnhorOffset,
        focusKey,
        focusOffset,
        length,
        { id: item.anchor.id, noteId: item.id },
      );
    }
  }

  updatedValue = JSON.stringify(rawValue);

  return updatedValue;
};

export const updateFormFieldsValueForNote = (allFormFields = [], comments) => {
  const formFields = allFormFields;
  if (Array.isArray(comments) && comments.length > 0) {
    comments.forEach(item => {
      const { noteMappingConfigs } = item.anchor;
      if (noteMappingConfigs && noteMappingConfigs.clauseOptionId) {
        formFields.forEach(field => {
          if (field.id === noteMappingConfigs.clauseOptionId) {
            // 1. For Clause options
            if (field.type === "CLAUSE") {
              const {
                meta: { subtype },
                value: values,
              } = field;
              if (subtype === "optional" || subtype === "multi_select") {
                if (
                  noteMappingConfigs.clauseOptionValueId &&
                  Array.isArray(values)
                )
                  values.forEach(value => {
                    if (value.id === noteMappingConfigs.clauseOptionValueId) {
                      value.value = applyNoteOnClauseOptionField(
                        value.value,
                        item,
                        noteMappingConfigs,
                      );
                    }
                  });
              } else if (subtype === "single_select") {
                if (values.id === noteMappingConfigs.clauseOptionValueId) {
                  values.value = applyNoteOnClauseOptionField(
                    values.value,
                    item,
                    noteMappingConfigs,
                  );
                }
              } else if (subtype === "free_text") {
                if (typeof values === "object") {
                  if (values.value) {
                    values.value = applyNoteOnClauseOptionField(
                      values.value,
                      item,
                      noteMappingConfigs,
                    );
                  }
                } else if (typeof values === "string") {
                  field.value = applyNoteOnClauseOptionField(
                    values,
                    item,
                    noteMappingConfigs,
                  );
                }
              }
            }
            // 2. For Table placeholder
            else if (field.type === "TABLE") {
              if (noteMappingConfigs.componentType === "Table") {
                const { tableRowId, tableColId } = noteMappingConfigs;
                const tableCellValue =
                  field.value.data[tableRowId].data[tableColId].data.data;
                const updatedTableCellValue = applyNoteOnClauseOptionField(
                  tableCellValue,
                  item,
                  noteMappingConfigs,
                );
                field.value.data[tableRowId].data[tableColId].data.data =
                  JSON.parse(updatedTableCellValue);
              }
            }
          }
        });
      }
    });
    // }
  }
  return formFields;
};
export const getComponentList = layout => {
  let componentList = [];
  layout.forEach(rowList => {
    if (typeof rowList === "string") componentList.push(rowList);
    else if (Array.isArray(rowList))
      componentList = [...componentList, ...rowList];
  });
  return componentList;
};

const _signatureIsAdded = tags => {
  if (tags && Array.isArray(tags) && tags.length > 0) {
    for (let i = 0; i < tags.length; i += 1) {
      const tag = tags[i];
      if (tag && tag.signer_config && tag.signer_config.options) {
        const {
          signer_config: { options },
        } = tag;
        if (options.find(op => op.id === "signature" && op.checked))
          return true;
      } else {
        return false;
      }
    }
  }
  return false;
};

// used for document : new signatories change
const _getOrderedSignersListV2 = signatoriesV2Original => {
  // First Remove tags which are not added
  const signatoriesV2 = signatoriesV2Original.filter(signatory =>
    _signatureIsAdded(signatory.tags),
  );
  const signersList = [];
  const signerEmails = new Set();
  for (let i = 0; i < signatoriesV2.length; i += 1) {
    const signatory = signatoriesV2[i];
    if (!signerEmails.has(signatory.signer_email)) {
      signerEmails.add(signatory.signer_email);
      const defaultSignerConfig = new SignerConfig();
      signersList.push({
        signer_email: signatory.signer_email,
        first_name: signatory.first_name,
        last_name: signatory.last_name,
        isTagAdded: signatory.tags.length > 0,
        occurences: signatory.tags.map(tag => tag.component_id),
        signer_config:
          signatory.tags.length > 0
            ? signatory.tags[0].signer_config
            : defaultSignerConfig.signer_config,
      });
    }
  }
  return signersList;
};

export const fetchSignersList = signatoriesV2 => {
  const signersList = _getOrderedSignersListV2(signatoriesV2);
  return signersList;
};

const _addSignerToList = (signers, signer, i, onErrorCallback) => {
  try {
    const {
      user_email,
      first_name,
      last_name,
      mobile_number,
      auth_type,
      sendSMS,
    } = signer;
    signers.push(
      new Signer(
        user_email,
        first_name,
        last_name,
        i + 1,
        mobile_number,
        auth_type,
        sendSMS,
      ).signer,
    );
  } catch (e) {
    // should have read error message of custom error
    onErrorCallback(e);
  }
};

const _addsubmitSignerToList = (signers, signer, i, onErrorCallback) => {
  try {
    const {
      user_email,
      first_name,
      last_name,
      mobile_number,
      auth_type,
      sendSMS,
    } = signer;
    signers.push(
      new SubmitSignatory(
        user_email,
        first_name,
        last_name,
        i + 1,
        mobile_number,
        auth_type,
        sendSMS,
      ).signer,
    );
  } catch (e) {
    // should have read error message of custom error
    onErrorCallback(e);
  }
};

export const getSignersPayload = (signersList, onErrorCallback) => {
  const signers = [];
  if (signersList.length > 0) {
    signersList.forEach((signer, i) => {
      _addSignerToList(signers, signer, i, onErrorCallback);
    });
  }
  return signers;
};

export const getSubmitSignersPayload = (signersList, onErrorCallback) => {
  const signers = [];
  if (signersList.length > 0) {
    signersList.forEach((signer, i) => {
      _addsubmitSignerToList(signers, signer, i, onErrorCallback);
    });
  }
  return signers;
};

export const getTableApisTable = fieldsUsed => {
  const tablePayload = [];
  const tableApiFields = fieldsUsed.filter(field => field.type === "TABLE_API");
  tableApiFields.forEach(field => {
    const { tables, value, label: fieldName, id: dynamicFieldId } = field;
    if (value && value.linked_table_id && tables[value.linked_table_id]) {
      tablePayload.push({
        ...tables[value.linked_table_id],
        tableAPIType: "linked_table_id",
        tableName: fieldName,
        associatedDFId: dynamicFieldId,
      });
    }
    if (value && value.unlinked_table_id && tables[value.unlinked_table_id]) {
      tablePayload.push({
        ...tables[value.unlinked_table_id],
        tableAPIType: "unlinked_table_id",
      });
    }
  });
  return tablePayload;
};
